import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { waLink } from "@/lib/api";
import { SERVICES } from "@/lib/services";
import QuoteForm from "@/components/QuoteForm";

const ADMIN_WA = "3499204449";
const LOGO = "/img/dads-logo.png";
const LOGO_SM = "/img/dads-logo-sm.png";
const TECH = [
  ["Bosch ESI[tronic]", "Diagnostica avanzata multimarca, schemi elettrici e protocolli ufficiali."],
  ["Sistemi CDR (Crash Data Retrieval)", "Estrazione forense certificata dei dati di crash (EDR) dalle centraline Airbag."],
  ["Diagnostica Autel", "Strumentazione di ultima generazione per codifiche, programmazioni e letture a banco."],
  ["Inforepair Quattroruote", "Banca dati ufficiale per procedure costruttore, bollettini tecnici e tempari."],
];
const PILLS = ["Bosch ESI[tronic]", "Sistemi CDR", "Autel", "Inforepair Quattroruote"];

function Brand({ testid }) {
  return (
    <a href="#top" className="flex items-center gap-2" data-testid={testid}>
      <span className="w-8 h-8 grid place-items-center bg-[#E50914] rounded-sm shrink-0"><Wrench size={17} strokeWidth={2.5} /></span>
      <span className="font-display text-xl font-extrabold tracking-wide uppercase whitespace-nowrap text-white">AutoTech<span className="text-[#E50914]">//</span>Pro</span>
    </a>
  );
}

function TopBar() {
  const { user } = useAuth();
  const area = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/login";
  return (
    <header className="sticky top-0 z-40 bg-[#121212]/90 backdrop-blur-md border-b border-[#2a2a2a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4 lg:gap-6">
        <div className="flex items-center gap-3 min-w-0">
          <Brand testid="landing-logo" />
          <span className="w-px h-7 bg-[#383838] shrink-0" />
          <span className="flex items-center gap-2 min-w-0" data-testid="navbar-dads-brand">
            <img src={LOGO_SM} alt="Dad's Garage Roots" className="h-8 w-[26px] object-contain shrink-0" />
            <span className="mono text-[10px] tracking-widest text-slate-400 uppercase hidden sm:inline whitespace-nowrap">by Dad's Garage Roots</span>
          </span>
        </div>
        <nav className="hidden lg:flex gap-6 text-sm text-slate-400">
          <a href="#servizi" className="hover:text-white" data-testid="landing-nav-servizi">Servizi</a>
          <a href="#perizie" className="hover:text-white" data-testid="landing-nav-perizie">Perizie e forense</a>
          <a href="#tradizione" className="hover:text-white" data-testid="landing-nav-tradizione">Chi siamo</a>
          <a href="#preventivo" className="hover:text-white" data-testid="landing-nav-preventivo">Preventivo</a>
        </nav>
        <Link to={area} className="ml-auto btn-red px-4 py-2 text-sm shrink-0 whitespace-nowrap" data-testid="landing-area-button">
          {user ? "Vai alla tua area" : "Accedi"}
        </Link>
      </div>
    </header>
  );
}

function useSupportLink() {
  const { user } = useAuth();
  if (!user) return "/registrati";
  return user.role === "admin" ? "/admin" : "/nuova-richiesta";
}

function Hero() {
  const supportLink = useSupportLink();
  return (
    <section id="top" className="relative overflow-hidden">
      <img src="/img/diagnosi.jpg" alt="Diagnosi elettronica" className="absolute inset-0 w-full h-full object-cover opacity-35" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/85 to-[#121212]/30" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
        <div className="eyebrow mb-5 fade-up">Diagnosi · Perizie · Forense automotive</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.92] max-w-3xl fade-up" data-testid="landing-title">
          Dalla diagnosi in officina<br /><span className="text-[#E50914]">alla perizia in tribunale.</span>
        </h1>
        <p className="text-slate-300 mt-6 max-w-xl leading-relaxed fade-up" style={{ animationDelay: "80ms" }}>
          Supporto tecnico remoto su appuntamento per autoriparatori, diagnosi dettagliata sul posto, perizie per privati, CTP e CTU, diagnosi forense delle centraline airbag con sistemi Bosch e letture a banco.
        </p>
        <div className="mt-7 max-w-2xl bg-[#1E1E1E]/85 border border-[#383838] border-l-4 border-l-[#E50914] rounded-sm px-4 py-3 fade-up" style={{ animationDelay: "120ms" }} data-testid="hero-direction-box">
          <div className="text-white font-bold text-sm sm:text-base uppercase tracking-wide">Direzione tecnica: Ing. Antonio Rotunno — Dad's Garage Roots</div>
          <div className="text-slate-300 text-xs sm:text-sm mt-1 leading-snug">Ingegnere Meccanico • Specialista Sistemi Elettronici &amp; Formatore Bosch • Tradizione meccatronica dal 1972 (Fond. Giuseppe Rotunno)</div>
        </div>
        <div className="flex flex-wrap gap-3 mt-9 fade-up" style={{ animationDelay: "160ms" }}>
          <Link to={supportLink} className="btn-red px-6 py-3.5 flex items-center gap-2" data-testid="hero-support-button">Prenota supporto remoto <ArrowRight size={17} /></Link>
          <a href="#preventivo" className="btn-ghost px-6 py-3.5" data-testid="hero-quote-button">Richiedi preventivo perizia</a>
        </div>
      </div>
      <div className="stripe-red h-1.5 w-full" />
    </section>
  );
}

function Services({ pick }) {
  const supportLink = useSupportLink();
  return (
    <section id="servizi" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="eyebrow mb-3">Cosa facciamo</div>
      <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase mb-12">Servizi</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
        {SERVICES.map((s, i) => (
          <div key={s.key} className="bg-[#161616] p-6 flex flex-col group hover:bg-[#1E1E1E] transition-colors fade-up" style={{ animationDelay: `${i * 50}ms` }}
            data-testid={`service-card-${s.key}`}>
            <div className="flex items-center justify-between mb-6">
              <s.icon className="text-[#E50914]" size={26} />
              <span className="tag text-slate-400">{s.tag}</span>
            </div>
            <h3 className="font-display text-xl font-bold uppercase leading-tight">{s.title}</h3>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed flex-1">{s.desc}</p>
            {s.app ? (
              <Link to={supportLink} className="text-sm text-[#E50914] font-semibold mt-6 flex items-center gap-1.5" data-testid={`service-cta-${s.key}`}>Apri un ticket <ArrowRight size={15} /></Link>
            ) : (
              <button onClick={() => pick(s.key)} className="text-sm text-[#E50914] font-semibold mt-6 flex items-center gap-1.5 text-left" data-testid={`service-cta-${s.key}`}>Richiedi preventivo <ArrowRight size={15} /></button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Forensic() {
  const items = [
    ["Privati", "Accertamenti sullo stato del veicolo, guasti contestati, acquisti e riparazioni."],
    ["CTP", "Consulente tecnico al fianco della parte, con analisi e relazione a supporto."],
    ["CTU", "Consulenza tecnica d'ufficio su incarico dei tribunali."],
  ];
  return (
    <section id="perizie" className="bg-[#0d0d0d] border-y border-[#2a2a2a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="eyebrow mb-3">Perizie e diagnosi forense</div>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase">I dati della centralina raccontano il sinistro</h2>
          <p className="text-slate-400 mt-5 leading-relaxed">Diagnosi forense delle centraline airbag con sistemi Bosch, letture a banco delle centraline e supporto tecnico alla ricostruzione del sinistro.</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-8">
            {items.map(([t, d]) => (
              <div key={t} className="surface p-4" data-testid={`perizia-type-${t.toLowerCase()}`}>
                <div className="font-display text-2xl font-extrabold text-[#E50914]">{t}</div>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <img src="/img/motore.jpg" alt="Vano motore" className="w-full aspect-[4/3] object-cover rounded-md border border-[#2a2a2a]" />
          <div className="absolute -bottom-5 left-5 right-5 surface-2 p-4 text-sm" data-testid="pricing-note">
            <div className="label">Costo medio perizia</div>
            <div className="text-white"><span className="mono text-2xl font-bold">€500</span> in Campania, Basilicata e Calabria</div>
            <div className="text-slate-400 text-xs mt-1">Altre regioni: + costi di spostamento, indicati nel preventivo.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Heritage() {
  const cards = [
    ["Formazione Ingegneristica", "Esperienza iniziata nell'officina storica di famiglia, consolidata con il Diploma di Tecnico dei Sistemi e delle Automazioni e la Laurea in Ingegneria Meccanica."],
    ["Carriera nell'Ecosistema Bosch", "Esperienza lavorativa qualificata come Tecnico Attrezzature Bosch, Tecnico Formatore, Tecnico Sistemi Elettronici Bosch e Direttore Tecnico Commerciale per concessionario Bosch S.p.A."],
    ["Servizi ad Alto Valore Tecnico", "Dalla diagnosi remota guidata per autoriparatori fino alle letture a banco di centraline, estrazione dati crash e consulenze tecniche per Tribunali (CTU, CTP) e privati."],
  ];
  return (
    <section id="tradizione" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="heritage-section">
      <div className="eyebrow mb-3">Dal 1972 a oggi • Dad's Garage Roots</div>
      <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase max-w-4xl">Oltre 50 anni di esperienza evoluta nell'ingegneria diagnostica.</h2>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center mt-10">
        <p className="text-slate-300 leading-relaxed text-base sm:text-lg" data-testid="heritage-text">
          AUTOTECH//PRO è la piattaforma digitale sviluppata e gestita da Dad's Garage Roots di Antonio Rotunno. La nostra competenza tecnica nasce dall'officina elettrauto e ricambi aperta nel 1972 da mio padre, Giuseppe Rotunno, ed è oggi sviluppata per i nostri tempi più tecnologici unendo l'esperienza sul campo all'ingegneria meccanica e all'analisi forense.
        </p>
        <div className="surface !bg-[#0a0a0a] p-8 sm:p-10 grid place-items-center" data-testid="heritage-logo-box">
          <img src={LOGO} alt="Dad's Garage Roots" className="w-48 sm:w-56 h-auto" />
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mt-10">
        {cards.map(([t, d], i) => (
          <div key={t} className="surface p-5" data-testid={`heritage-card-${i + 1}`}>
            <div className="font-display text-2xl font-extrabold text-[#E50914] uppercase leading-tight">{t}</div>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
      <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase mt-14 mb-5">Attrezzature e banche dati di ultima generazione</h3>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="tech-grid">
        {TECH.map(([t, d], i) => (
          <div key={t} className="surface-2 p-5 border-l-4 !border-l-[#E50914]" data-testid={`tech-card-${i + 1}`}>
            <div className="mono text-sm font-bold text-white tracking-wide">{t}</div>
            <p className="text-sm text-slate-400 mt-2">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  const h = "mono text-xs tracking-[0.2em] uppercase text-[#E50914] mb-4";
  return (
    <footer className="border-t border-[#2a2a2a] bg-[#0d0d0d]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-3 gap-10 text-sm text-slate-400">
        <div data-testid="footer-brand">
          <div className="flex items-center gap-4">
            <Brand testid="footer-logo" />
            <span className="w-px h-10 bg-[#383838]" />
            <img src={LOGO_SM} alt="Dad's Garage Roots" className="h-12 w-auto" />
          </div>
          <p className="mt-5 leading-relaxed">Servizio di proprietà di Dad's Garage Roots di Ing. Antonio Rotunno. Evoluzione tecnologica dell'officina elettrauto e ricambi fondata nel 1972 da Giuseppe Rotunno.</p>
        </div>
        <div data-testid="footer-direction">
          <div className={h}>Direzione tecnica</div>
          <p className="leading-relaxed">Ing. Antonio Rotunno — Laurea in Ingegneria Meccanica • Tecnico dei Sistemi e delle Automazioni • Background certificato Bosch (Tecnico Attrezzature, Formatore, Sistemi Elettronici, Dir. Tecnico Commerciale).</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {PILLS.map((p) => <span key={p} className="tag text-slate-300">{p}</span>)}
          </div>
        </div>
        <div data-testid="footer-contacts">
          <div className={h}>Sede operativa &amp; contatti</div>
          <div className="space-y-1.5 leading-relaxed">
            <div className="text-white font-semibold">Dad's Garage Roots di Antonio Rotunno</div>
            <div>Via Nazionale 527 — Padula (SA)</div>
            <div>Tel / WhatsApp: <a href={waLink(ADMIN_WA, "Salve, vorrei informazioni sui vostri servizi.")} target="_blank" rel="noreferrer" className="text-white hover:text-[#25D366]" data-testid="footer-whatsapp-button">349 920 4449</a></div>
            <div>Email: <a href="mailto:Dadsgarageroots@gmail.com" className="text-white hover:text-[#E50914]" data-testid="footer-email-link">Dadsgarageroots@gmail.com</a></div>
            <div>Web: <a href="https://dadsgarageroots.com" target="_blank" rel="noreferrer" className="text-white hover:text-[#E50914]" data-testid="footer-web-link">dadsgarageroots.com</a></div>
          </div>
        </div>
      </div>
      <div className="border-t border-[#2a2a2a] py-4 text-center mono text-[11px] text-slate-500">AUTOTECH//PRO · by Dad's Garage Roots</div>
    </footer>
  );
}

export default function Landing() {
  const [service, setService] = useState("perizia_privati");
  const pick = (k) => {
    setService(k);
    document.getElementById("preventivo")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <div className="min-h-screen">
      <TopBar />
      <Hero />
      <Services pick={pick} />
      <Forensic />
      <Heritage />
      <section id="preventivo" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="eyebrow mb-3">Gratuito e senza impegno</div>
        <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase mb-3">Richiedi un preventivo</h2>
        <p className="text-slate-400 mb-10">Perizie, CTP, CTU, diagnosi sul posto e analisi forense: descrivi il caso e ti rispondiamo con un preventivo.</p>
        <QuoteForm service={service} setService={setService} />
      </section>
      <Footer />
    </div>
  );
}
